import { LevelDesignerElementBase } from "../elements/element-base";
import { LevelDesignerComponent } from "../level-designer/level-designer.component";
import { Tool } from "./tool";
import { EventEmitter } from '@angular/core';
import { ToolType } from "./tool";
import { ActionTool } from "./tool-base-types";
import { VisualTool } from "./tool-base-types";
import { ToolGroup } from './tool-group';
import { ToolButton } from './tool-button';
import { EmbossedRectangle } from '../utils/embossed-rectangle';
import { MouseEventResult } from "../managers/manager-base";

export class Toolbar {
  private ctx: CanvasRenderingContext2D;
  private width: number;
  private height: number = 40; // Match other components
  private x: number = 10;
  private y: number = 10;
  private tools: Tool[] = [];
  public onToolActivated = new EventEmitter<Tool>();
  private tooltipTimer: any = null;
  private hoveredTool: Tool | null = null;
  private groups: ToolGroup[] = [];
  private readonly TOOLTIP_DELAY = 500;
  private toolGroups: ToolGroup[] = [];
  private activeActionTool: ActionTool | null = null;
  private toolButtons: Map<Tool, ToolButton> = new Map();

  constructor(private levelDesigner: LevelDesignerComponent, private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.width = canvas.width;
    this.draw();

    this.canvas.addEventListener('mousemove', (e) => this.handleTooltip(e));
    this.canvas.addEventListener('mouseout', () => this.clearTooltip());
  }

  public setPosition(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  public setDimensions(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  public Refresh() {
    this.draw();
  }

  private draw() {
    // Create gradient for 3D effect
    const gradient = this.ctx.createLinearGradient(this.x, this.y, this.x, this.y + this.height);
    gradient.addColorStop(0, '#E8E8E8');
    gradient.addColorStop(1, '#C8C8C8');

    // Draw toolbar background with gradient and rounded corners
    EmbossedRectangle.draw(this.ctx, this.x, this.y, this.width, this.height, {
        topLeft: 5,
        topRight: 5,
        bottomRight: 5,
        bottomLeft: 5
    }, gradient);

    // Draw tools using their own drawButton method and width
    const toolHeight = 30;
    const toolPadding = 5;
    let currentX = this.x + 5;

    for (const group of this.groups) {
      // Draw tools in this group
      group.getTools().forEach(tool => {
        const toolWidth = tool.getButtonWidth();
        tool.boundary = {
          x: currentX,
          y: this.y + 5,
          width: toolWidth,
          height: toolHeight
        };

        // Get or create ToolButton for this tool
        let toolButton = this.toolButtons.get(tool);
        if (!toolButton) {
          toolButton = new ToolButton(tool);
          this.toolButtons.set(tool, toolButton);
        }

        // Draw the tool button
        toolButton.draw(this.ctx, currentX, this.y + 5, toolWidth, toolHeight);
        currentX += toolWidth + toolPadding;
      });
      // Add spacing between groups
      currentX += 15;
    }
  }

  // Method to update the toolbar if canvas size changes
  public update() {
    this.width = this.canvas.width;
    this.draw();
  }

  // Add this new method
  public addTool(tool: Tool) {
    if (tool instanceof ActionTool) {
        tool.onActivate.subscribe(() => {
            if (this.activeActionTool && this.activeActionTool !== tool) {
                this.activeActionTool.deactivate();
            }
            this.activeActionTool = tool as ActionTool;
        });
    }
    this.tools.push(tool);
    this.draw();
  }

  public isPointInside(x: number, y: number): boolean {
    return x >= this.x && x <= this.x + this.width &&
           y >= this.y && y <= this.y + this.height;
  }

  public handleClick(x: number, y: number): void {
    for (const group of this.groups) {
        const clickedTool = group.getTools().find(tool => tool.isPointInside(x, y));
        if (clickedTool) {
            group.handleClick(clickedTool);
            this.onToolActivated.emit(clickedTool);
            this.levelDesigner.redraw();
            break;
        }
    }
  }

  public notifyToolsOfSelectionChange(selectedElements: LevelDesignerElementBase[]): void {
    this.groups.forEach(group => {
        group.getTools().forEach(tool => {
            tool.onSelectionChanged(selectedElements);
        });
    });
  }

  public handleMouseMove(x: number, y: number): MouseEventResult | null {
    for (const group of this.groups) {
        const result = group.handleMouseMove(x, y);
        if (result) {
            return result;
        }
    }
    return null;
  }

  public handleMouseDown(x: number, y: number): MouseEventResult | null {
    for (const group of this.groups) {
        const result = group.handleMouseDown(x, y);
        if (result) {
            return result;
        }
    }
    return  null;
  }

  public handleMouseUp(x: number, y: number): MouseEventResult | null {
    for (const group of this.groups) {
        const result = group.handleMouseUp(x, y);
        if (result) {
            return result;
        }
    }
    return null;
  }

  public addGroup(name: string): ToolGroup {
    const group = new ToolGroup(name);
    this.groups.push(group);
    return group;
  }

  public getActiveTools(): Tool[] {
    return this.groups
        .flatMap(group => group.getTools())
        .filter(tool => tool.isActive);
  }

  public cancelAction(): void {
    for (const group of this.groups) {
      group.cancelAction();
    }
  }

  private handleTooltip(event: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Clear any existing tooltip timer
    this.clearTooltip();

    // Find the tool being hovered
    const hoveredTool = this.groups
      .flatMap(group => group.getTools())
      .find(tool => tool.isPointInside(x, y));

    if (hoveredTool) {
      this.hoveredTool = hoveredTool;
      this.tooltipTimer = setTimeout(() => {
        this.showTooltip(hoveredTool, event.clientX, event.clientY);
      }, this.TOOLTIP_DELAY);
    }
  }

  private showTooltip(tool: Tool, clientX: number, clientY: number): void {
    // Create or get existing tooltip element
    let tooltip = document.getElementById('levelDesignerTooltip');
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.id = 'levelDesignerTooltip';
      document.body.appendChild(tooltip);
      
      // Style the tooltip
      tooltip.style.position = 'fixed';
      tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
      tooltip.style.color = 'white';
      tooltip.style.padding = '5px 10px';
      tooltip.style.borderRadius = '4px';
      tooltip.style.fontSize = '12px';
      tooltip.style.zIndex = '1000';
      tooltip.style.pointerEvents = 'none';
    }

    tooltip.textContent = tool.getTooltip();
    tooltip.style.display = 'block';
    
    // Position the tooltip slightly below and to the right of the cursor
    tooltip.style.left = `${clientX + 10}px`;
    tooltip.style.top = `${clientY + 10}px`;
  }

  private clearTooltip(): void {
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
      this.tooltipTimer = null;
    }
    this.hoveredTool = null;

    const tooltip = document.getElementById('levelDesignerTooltip');
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  }

  // Add to existing destroy/cleanup method, or create one if it doesn't exist
  public destroy(): void {
    this.clearTooltip();
    const tooltip = document.getElementById('levelDesignerTooltip');
    if (tooltip) {
      document.body.removeChild(tooltip);
    }
  }

  public handleDoubleClick(x: number, y: number): MouseEventResult | undefined {
    // Check if click is on any tool
    for (const group of this.toolGroups) {
      for (const tool of group.getTools()) {
        if (tool.isPointInside(x, y)) {
          // Double click on a tool - do nothing by default
          return { cursor: 'default', stopPropagation: true, redraw: false };
        }
      }
    }
    return undefined;
  }
}
